import type {
    CausalEffectRow,
    CausalIncremental,
    CausalMetricKey,
    CausalModelledKey,
    ExperimentDailyRow,
    StrategyActivation,
} from "src/context/api/bidStratTesting/api";
import { monthNames, tr } from "./i18n";

export const DEFAULT_COMPARISON_WEEKS = 8;
export const strategyNotFound = () => tr("strategy.not_found");
export const unreliableComparison = () => tr("modal.unreliable");

export type MetricKey =
    | "ad_cost"
    | "blended_ctr"
    | "blended_cpc"
    | "brand_traffic"
    | "brand_searches"
    | "paid_clicks"
    | "organic_clicks"
    | "paid_conversions"
    | "paid_cvr";
export type MetricUnit = "currency" | "percent" | "number";

interface MetricDef {
    readonly label: string;
    unit: MetricUnit;
    // Ratio on the sums: the same in Total, Weekly and Daily, never a mean
    ratio: boolean;
    color: string;
    // A rise is bad news, shown in red (Ad Cost, Blended CPC)
    lowerIsBetter: boolean;
}

// Order and line colours of the Figma metric dropdown, then the metrics of the
// user feedback ticket. `label` is read in the current language (getter)
const metricDef = (key: MetricKey, unit: MetricUnit, ratio: boolean, color: string, lowerIsBetter = false): MetricDef => ({
    get label() {
        return tr(`metrics.${key}`);
    },
    unit,
    ratio,
    color,
    lowerIsBetter,
});
export const METRICS: Record<MetricKey, MetricDef> = {
    brand_traffic: metricDef("brand_traffic", "number", false, "#637381"),
    ad_cost: metricDef("ad_cost", "currency", false, "#DAA335", true),
    blended_ctr: metricDef("blended_ctr", "percent", true, "#5119B7"),
    blended_cpc: metricDef("blended_cpc", "currency", true, "#D42186", true),
    brand_searches: metricDef("brand_searches", "number", false, "#EE7A63"),
    paid_clicks: metricDef("paid_clicks", "number", false, "#DAA335"),
    // "Organic" green of the Traffic page (funnel/constants.ts)
    organic_clicks: metricDef("organic_clicks", "number", false, "#18A055"),
    paid_conversions: metricDef("paid_conversions", "number", false, "#DAA335"),
    paid_cvr: metricDef("paid_cvr", "percent", true, "#DAA335"),
};
export const METRIC_KEYS = Object.keys(METRICS) as MetricKey[];
// Recap grid column order
export const RECAP_METRIC_KEYS: MetricKey[] = [
    "ad_cost",
    "blended_ctr",
    "blended_cpc",
    "brand_traffic",
    "paid_clicks",
    "organic_clicks",
    "brand_searches",
    "paid_conversions",
    "paid_cvr",
];
// Hidden until shown with the "Columns" button of the grid
export const HIDDEN_RECAP_METRIC_KEYS: MetricKey[] = ["paid_clicks", "organic_clicks", "paid_conversions", "paid_cvr"];
// Columns with the (i) of the Brand Traffic composition
export const TRAFFIC_METRIC_KEYS: MetricKey[] = ["brand_traffic", "paid_clicks", "organic_clicks"];

export type DisplayMode = "total" | "weekly" | "daily";
export type GroupBy = "daily" | "weekly";

type VolumeKey = "ad_cost" | "brand_traffic" | "brand_searches" | "paid_clicks" | "organic_clicks" | "paid_conversions";
type RatioKey = "blended_ctr" | "blended_cpc" | "paid_cvr";
const VOLUME_KEYS: VolumeKey[] = ["ad_cost", "brand_traffic", "brand_searches", "paid_clicks", "organic_clicks", "paid_conversions"];

export type Totals = Record<VolumeKey, number>;
export type Ratios = Record<RatioKey, number | null>;

export interface Summary extends Totals, Ratios {
    days: number;
    start: string | null;
    end: string | null;
}

/**
 * Ratios, always on sums: CPC = cost / traffic, CTR capped at 100 %, CVR Paid =
 * conversions / paid clicks.
 */
export function ratios({ ad_cost, brand_traffic, brand_searches, paid_clicks, paid_conversions }: Totals): Ratios {
    return {
        blended_cpc: brand_traffic > 0 ? ad_cost / brand_traffic : null,
        blended_ctr: brand_searches > 0 ? Math.min(brand_traffic / brand_searches, 1) : null,
        paid_cvr: paid_clicks > 0 ? paid_conversions / paid_clicks : null,
    };
}

const ZERO_TOTALS = Object.fromEntries(VOLUME_KEYS.map((key) => [key, 0])) as Totals;

function sumRows(rows: ExperimentDailyRow[]): Totals {
    return rows.reduce(
        (acc, row) =>
            Object.fromEntries(VOLUME_KEYS.map((key) => [key, acc[key] + (row[key] ?? 0)])) as Totals,
        ZERO_TOTALS
    );
}

/** Before or After only: excluded days (start of the launch week) never count. */
export function summarizePeriod(rows: ExperimentDailyRow[], period: "before" | "after"): Summary {
    const periodRows = rows.filter((row) => row.period === period);
    const totals = sumRows(periodRows);
    return {
        ...totals,
        ...ratios(totals),
        days: periodRows.length,
        start: periodRows[0]?.date_day ?? null,
        end: periodRows[periodRows.length - 1]?.date_day ?? null,
    };
}

/** Total: sum. Weekly: sum / days × 7. Daily: sum / days. Ratios never change. */
export function normalize(summary: Summary, mode: DisplayMode): Summary {
    if (mode === "total" || summary.days === 0) return summary;
    const factor = (mode === "weekly" ? 7 : 1) / summary.days;
    return {
        ...summary,
        ...(Object.fromEntries(VOLUME_KEYS.map((key) => [key, summary[key] * factor])) as Totals),
    };
}

export function difference(before: number | null, after: number | null, percent: boolean): number | null {
    if (before === null || after === null) return null;
    if (!percent) return after - before;
    return before === 0 ? null : (after - before) / before;
}

/** Monday of the ISO week, as yyyy-mm-dd (dates are UTC calendar days). */
export function isoWeekStart(day: string): string {
    const date = new Date(`${day}T00:00:00Z`);
    const offset = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    return date.toISOString().slice(0, 10);
}

export interface SeriesPoint extends Totals, Ratios {
    x: string;
}

/** Chart points. Weekly points sum the days of each ISO week, ratios recomputed on the sums. */
export function groupSeries(rows: ExperimentDailyRow[], groupBy: GroupBy): SeriesPoint[] {
    const buckets = new Map<string, ExperimentDailyRow[]>();
    rows.forEach((row) => {
        const key = groupBy === "weekly" ? isoWeekStart(row.date_day) : row.date_day;
        buckets.set(key, [...(buckets.get(key) ?? []), row]);
    });
    return Array.from(buckets.entries()).map(([x, bucket]) => {
        const totals = sumRows(bucket);
        return { x, ...totals, ...ratios(totals) };
    });
}

// ---------------------------------------------------------------------------
// Statistical graph (lot 2, Causal Impact)

// Brand Searches is neither modelled nor derived
export const STATISTICAL_METRIC_KEYS: CausalMetricKey[] = [
    "brand_traffic",
    "ad_cost",
    "blended_ctr",
    "blended_cpc",
    "paid_clicks",
    "organic_clicks",
    "paid_conversions",
    "paid_cvr",
];
const MODELLED_KEYS: CausalModelledKey[] = ["brand_traffic", "ad_cost", "paid_clicks", "organic_clicks", "paid_conversions"];
export const MIN_POST_DAYS = 14;
export const unreliableStatistical = () => tr("analysis.unreliable");
const SIGNIFICANCE_THRESHOLD = 0.05;

export interface CausalValue {
    actual: number | null;
    predicted: number | null;
    lower_bound: number | null;
    upper_bound: number | null;
}

export interface CausalPoint {
    x: string;
    values: Record<CausalMetricKey, CausalValue>;
}

const EMPTY_VALUE: CausalValue = { actual: null, predicted: null, lower_bound: null, upper_bound: null };

const sumOrNull = (values: (number | null)[]) =>
    values.some((value) => value === null) ? null : values.reduce<number>((acc, value) => acc + (value as number), 0);

const safeDivide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);
const capped = (value: number | null) => (value === null ? null : Math.min(value, 1));
const nonNegative = (value: number | null) => (value === null ? null : Math.max(value, 0));

/**
 * Chart points. Daily: the view rows as they are. Weekly: the modelled metrics
 * summed over the ISO week, Blended CTR, Blended CPC and CVR Paid derived from
 * those sums with the view formulas. In the launch week, the days before the start date have no
 * prediction: their counterfactual is the actual value.
 */
export function groupCausalSeries(rows: CausalEffectRow[], dailyRows: ExperimentDailyRow[], groupBy: GroupBy): CausalPoint[] {
    const byDay = new Map<string, Record<CausalMetricKey, CausalValue>>();
    rows.forEach((row) => {
        const values = byDay.get(row.date_day) ?? ({} as Record<CausalMetricKey, CausalValue>);
        values[row.metric] = {
            actual: row.actual,
            predicted: row.predicted,
            lower_bound: row.lower_bound,
            upper_bound: row.upper_bound,
        };
        byDay.set(row.date_day, values);
    });
    const days = Array.from(byDay.keys()).sort();
    const complete = (values: Partial<Record<CausalMetricKey, CausalValue>>) =>
        Object.fromEntries(STATISTICAL_METRIC_KEYS.map((key) => [key, values[key] ?? EMPTY_VALUE])) as Record<
            CausalMetricKey,
            CausalValue
        >;
    if (groupBy === "daily") return days.map((x) => ({ x, values: complete(byDay.get(x)!) }));

    const searches = new Map(dailyRows.map((row) => [row.date_day, row.brand_searches]));
    const weeks = new Map<string, string[]>();
    days.forEach((day) => {
        const week = isoWeekStart(day);
        weeks.set(week, [...(weeks.get(week) ?? []), day]);
    });
    return Array.from(weeks.entries()).map(([x, weekDays]) => {
        const modelled = (metric: CausalModelledKey) => {
            const values = weekDays.map((day) => byDay.get(day)![metric] ?? EMPTY_VALUE);
            const anyPredicted = values.some((value) => value.predicted !== null);
            const counterfactual = (field: "predicted" | "lower_bound" | "upper_bound") =>
                anyPredicted ? sumOrNull(values.map((value) => (value.predicted === null ? value.actual : value[field]))) : null;
            return {
                actual: sumOrNull(values.map((value) => value.actual)),
                predicted: counterfactual("predicted"),
                lower_bound: counterfactual("lower_bound"),
                upper_bound: counterfactual("upper_bound"),
            };
        };
        const sums = Object.fromEntries(MODELLED_KEYS.map((key) => [key, modelled(key)])) as Record<CausalModelledKey, CausalValue>;
        const traffic = sums.brand_traffic;
        const cost = sums.ad_cost;
        const weekSearches = weekDays.reduce((acc, day) => acc + (searches.get(day) ?? 0), 0);
        const ctr = (value: number | null) => capped(safeDivide(value, weekSearches));
        // Ratio of two modelled sums, its bounds approximated with the opposite bounds
        const ratio = (numerator: CausalValue, denominator: CausalValue): CausalValue => ({
            actual: safeDivide(numerator.actual, denominator.actual),
            predicted: safeDivide(numerator.predicted, denominator.predicted),
            lower_bound: safeDivide(nonNegative(numerator.lower_bound), denominator.upper_bound),
            upper_bound:
                denominator.lower_bound !== null && denominator.lower_bound > 0
                    ? safeDivide(numerator.upper_bound, denominator.lower_bound)
                    : null,
        });
        return {
            x,
            values: {
                ...sums,
                blended_ctr: {
                    actual: ctr(traffic.actual),
                    predicted: ctr(traffic.predicted),
                    lower_bound: nonNegative(ctr(traffic.lower_bound)),
                    upper_bound: ctr(traffic.upper_bound),
                },
                blended_cpc: ratio(cost, traffic),
                paid_cvr: ratio(sums.paid_conversions, sums.paid_clicks),
            },
        };
    });
}

const incrementalMetric = (incremental: CausalIncremental, key: CausalMetricKey) =>
    incremental.metrics.find((metric) => metric.metric === key);

/**
 * Effect attributed to the strategy change: p-value below 0.05. Otherwise (or
 * without a p-value) the Incremental % is shown in grey. A ratio is read with
 * the highest p-value of the metrics it is made of (API).
 */
export function isEffectConfirmed(incremental: CausalIncremental, key: CausalMetricKey): boolean {
    const pValue = incrementalMetric(incremental, key)?.p_value;
    return pValue !== null && pValue !== undefined && pValue < SIGNIFICANCE_THRESHOLD;
}

/**
 * Incremental value and % of a metric. Volumes follow the Daily / Weekly / Total
 * mode, as the Before and After rows; ratios never change.
 */
export function incrementalValue(
    incremental: CausalIncremental,
    key: CausalMetricKey,
    mode: DisplayMode
): { value: number | null; pct: number | null } {
    const metric = incrementalMetric(incremental, key);
    if (!metric) return { value: null, pct: null };
    const factor =
        METRICS[key].ratio || mode === "total" || !incremental.post_days ? 1 : (mode === "weekly" ? 7 : 1) / incremental.post_days;
    return { value: metric.value === null ? null : metric.value * factor, pct: metric.pct };
}

/** Error of the modelled metrics a metric depends on, null if none */
export function causalStatus(status: Partial<Record<CausalModelledKey, string | null>>, key: CausalMetricKey): string | null {
    const SOURCES: Record<CausalMetricKey, CausalModelledKey[]> = {
        brand_traffic: ["brand_traffic"],
        ad_cost: ["ad_cost"],
        paid_clicks: ["paid_clicks"],
        organic_clicks: ["organic_clicks"],
        paid_conversions: ["paid_conversions"],
        blended_ctr: ["brand_traffic"],
        blended_cpc: ["ad_cost", "brand_traffic"],
        paid_cvr: ["paid_conversions", "paid_clicks"],
    };
    const sources = SOURCES[key];
    const errors = sources.filter((source) => status[source]).map((source) => `${METRICS[source].label}: ${status[source]}`);
    return errors.length ? errors.join(" · ") : null;
}


const parseDay = (day: string) => {
    const [year, month, date] = day.split("-").map(Number);
    return { year, month, date };
};

/** 2026-10-25 → "25 Oct 2026" */
export function formatDay(day: string | null | undefined): string {
    if (!day) return "--";
    const { year, month, date } = parseDay(day);
    return `${String(date).padStart(2, "0")} ${monthNames()[month - 1]} ${year}`;
}

/** "25 Mar 2026 - 25 Jun 2026" */
export function formatRange(start: string | null, end: string | null): string {
    if (!start) return "--";
    return `${formatDay(start)} - ${end ? formatDay(end) : "--"}`;
}

/** Recap grid dates, year once when shared: "25 Mar - 25 May 2026" */
export function formatShortRange(start: string | null, end: string | null): string {
    if (!start || !end) return "--";
    const from = parseDay(start);
    const to = parseDay(end);
    const head = `${String(from.date).padStart(2, "0")} ${monthNames()[from.month - 1]}`;
    return from.year === to.year ? `${head} - ${formatDay(end)}` : `${formatDay(start)} - ${formatDay(end)}`;
}

/** Block titles: "Ongoing experiments (03)" */
export const padCount = (count: number) => String(count).padStart(2, "0");

/** Comparison period column, in days as in the design */
export const comparisonPeriodLabel = (weeks: number) => tr("table.days", { count: weeks * 7 });

const KEYWORD_LEVEL_MODES = ["active mode", "ko bidding manual", "ko bidding auto"];

/**
 * Bid strategy of the activations, not their Signal mode: the page compares bid
 * strategy changes. Keyword-level modes (Active Mode, KO Bidding) have no type on
 * old lines: they run on Max CPC. The mode stays as a last resort.
 */
export function strategyLabel(strategy: StrategyActivation[] | null | undefined): string {
    if (!strategy || strategy.length === 0) return strategyNotFound();
    const labels = strategy.map((activation) => {
        if (activation.bidding_strategy_type) return activation.bidding_strategy_type;
        const mode = (activation.mode ?? "").split(" - ")[0].trim();
        if (KEYWORD_LEVEL_MODES.includes(mode.toLowerCase())) return "Max CPC";
        return activation.mode || strategyNotFound();
    });
    return Array.from(new Set(labels)).join(", ");
}

// Error codes of the API with a translated message; the others show the API's text
const API_ERROR_CODES = [
    "start_date_in_past",
    "strategy_required",
    "campaign_not_found",
    "experiment_already_running",
    "experiment_already_scheduled",
    "max_cpc_search_only",
    "keyword_not_found",
    "keyword_not_eligible",
    "experiment_not_running",
    "experiment_not_scheduled",
];

/** Error of the API in the page language, null when it has no translation. */
export function apiErrorMessage(error: any): string | null {
    const code = error?.data?.code;
    if (code && API_ERROR_CODES.includes(code)) return tr(`modal.errors.${code}`);
    // Payload refused by the schema: no code, a generic English message
    if (error?.data?.error === "Invalid request") return tr("modal.errors.invalid_request");
    return null;
}

/** "Comparison strategies" cell: the reference (before) vs. the tested strategy (after). */
export function comparisonStrategies(
    before: StrategyActivation[] | null | undefined,
    after: StrategyActivation[] | null | undefined
): { tested: string; reference: string } | null {
    if (!before?.length && !after?.length) return null;
    return { tested: strategyLabel(after), reference: strategyLabel(before) };
}

export const comparisonStrategiesText = (
    before: StrategyActivation[] | null | undefined,
    after: StrategyActivation[] | null | undefined
) => {
    const strategies = comparisonStrategies(before, after);
    return strategies ? `${strategies.reference} ${tr("common.vs")} ${strategies.tested}` : strategyNotFound();
};

/** yyyy-mm-dd in local time, the calendar day the user picked. */
export function toIsoDate(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today in Paris (yyyy-mm-dd), the day of the API and of its jobs, whatever the browser's time zone. */
export function parisToday(now: Date = new Date()): string {
    // en-CA formats as yyyy-mm-dd
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(now);
}

/** "Create experiment" (already finished) vs "Select Strategy" (lot 1b). */
export function isEndDatePast(endDate: Date | null, today: string = parisToday()): boolean {
    return !!endDate && toIsoDate(endDate) < today;
}

export function formatMetric(value: number | null, unit: MetricUnit, currency: string, locale: string): string {
    if (value === null || Number.isNaN(value)) return "--";
    if (unit === "percent") return formatPercent(value);
    if (unit === "currency") return formatCurrencyValue(value, currency, locale);
    return Math.round(value).toLocaleString(locale);
}

function formatCurrencyValue(value: number, currency: string, locale: string): string {
    try {
        return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
    } catch {
        return value.toFixed(2);
    }
}

/** 0.24 → "24%", 0.245 → "24.5%" */
export const formatPercent = (value: number) => `${Number((value * 100).toFixed(1))}%`;

export function formatDifference(
    value: number | null,
    unit: MetricUnit,
    percent: boolean,
    currency: string,
    locale: string
): string {
    if (value === null || Number.isNaN(value)) return "--";
    const sign = value > 0 ? "+" : "";
    if (percent) return `${sign}${formatPercent(value)}`;
    // A CTR gap is in percentage points, "-45pp" (bug report #15)
    if (unit === "percent") return `${sign}${Number((value * 100).toFixed(1))}pp`;
    return `${sign}${formatMetric(value, unit, currency, locale)}`;
}

// ---------------------------------------------------------------------------
// Strategy "View" modal (Figma "Before parameters - all strategy modals")

const first = (...values: unknown[]) => values.find((value) => value !== undefined && value !== null && value !== "");

const asText = (value: unknown) => (Array.isArray(value) ? value.join(", ") : String(value));

/** Google Ads notation: [exact], “phrase”, broad */
export function formatKeyword(keyword: unknown, matchType: unknown): string {
    if (!keyword) return "";
    const type = String(matchType ?? "").toUpperCase();
    if (type === "EXACT") return `[${keyword}]`;
    if (type === "PHRASE") return `“${keyword}”`;
    return String(keyword);
}

type ParameterColumn = { key: string; label: string; numeric: boolean; value: (a: StrategyActivation) => unknown };

const COLUMN: Record<string, ParameterColumn> = {
    keyword: { key: "keyword", get label() { return tr("parameters.keyword"); }, numeric: false, value: (a) => formatKeyword(a.parameters.keyword, a.parameters.match_type) },
    campaign: { key: "campaign", get label() { return tr("parameters.campaign"); }, numeric: false, value: (a) => a.parameters.campaign_name },
    ad_group: { key: "ad_group", get label() { return tr("parameters.ad_group"); }, numeric: false, value: (a) => a.parameters.ad_group_name },
    kpi: {
        key: "kpi",
        get label() { return tr("parameters.kpi"); },
        numeric: false,
        // "KO Bidding Auto - Smart Top" → "Smart Top"
        value: (a) => ((a.mode ?? "").includes(" - ") ? (a.mode ?? "").split(" - ").slice(1).join(" - ") : undefined),
    },
    position: { key: "position", get label() { return tr("parameters.position"); }, numeric: false, value: (a) => a.parameters.target_impression_share_position },
    tis: {
        key: "tis",
        get label() { return tr("parameters.tis"); },
        numeric: true,
        value: (a) => (a.parameters.target_impression_share != null ? `${a.parameters.target_impression_share}%` : undefined),
    },
    target_cpa: {
        key: "target_cpa",
        get label() { return tr("parameters.target_cpa"); },
        numeric: true,
        value: (a) => first(a.parameters.target_cpa, a.parameters.target_cpa_portfolio_strategy),
    },
    target_roas: {
        key: "target_roas",
        get label() { return tr("parameters.roas"); },
        numeric: true,
        value: (a) => first(a.parameters.target_roas, a.parameters.target_roas_portfolio_strategy),
    },
    max_cpc: {
        key: "max_cpc",
        get label() { return tr("parameters.max_cpc"); },
        numeric: true,
        value: (a) => first(a.parameters.max_cpc, a.parameters.max_cpc_portfolio_strategy),
    },
    threshold: {
        key: "threshold",
        get label() { return tr("parameters.threshold"); },
        numeric: true,
        value: (a) => (a.parameters.variation_threshold != null ? `${a.parameters.variation_threshold}%` : undefined),
    },
};

// Figma "Before parameters - all strategy modals": the columns of each strategy
const STRATEGY_GROUPS: Record<string, { title: (a: StrategyActivation) => string; columns: ParameterColumn[] }> = {
    active: {
        title: () => tr("parameters.title_active"),
        columns: [COLUMN.keyword, COLUMN.campaign, COLUMN.ad_group, COLUMN.max_cpc],
    },
    ko_manual: {
        title: () => tr("parameters.title_ko_manual"),
        columns: [COLUMN.keyword, COLUMN.campaign, COLUMN.ad_group, COLUMN.max_cpc],
    },
    ko_auto: {
        title: () => tr("parameters.title_ko_auto"),
        columns: [COLUMN.keyword, COLUMN.campaign, COLUMN.ad_group, COLUMN.kpi, COLUMN.max_cpc, COLUMN.threshold],
    },
    cpa: { title: (a) => tr("parameters.title", { strategy: a.bidding_strategy_type }), columns: [COLUMN.campaign, COLUMN.target_cpa, COLUMN.max_cpc] },
    tis: {
        title: () => tr("parameters.title_tis"),
        columns: [COLUMN.campaign, COLUMN.ad_group, COLUMN.position, COLUMN.tis, COLUMN.max_cpc],
    },
    roas: {
        title: (a) => tr("parameters.title", { strategy: a.bidding_strategy_type }),
        columns: [COLUMN.campaign, COLUMN.ad_group, COLUMN.target_roas, COLUMN.max_cpc],
    },
};

/**
 * Signal activation (line of scraping / bo_activations, with its mode), unlike the
 * strategy read in Google Ads (G-READ, no mode), which only names the strategy.
 */
const isSignalActivation = (activation: StrategyActivation) => !!activation.mode;

/** "View" of the Parameters column: only when the strategy ran with Signal (Lily). */
export const hasSignalActivation = (strategy: StrategyActivation[] | null | undefined) =>
    (strategy ?? []).some(isSignalActivation);

function strategyGroup(activation: StrategyActivation): string {
    const type = activation.bidding_strategy_type ?? "";
    // Campaign-level strategies first: their line is in KO Bidding Manual mode
    if (type === "Target ROAS" || type === "Maximize Conversion Value") return "roas";
    if (type === "Target CPA" || type === "Maximize Conversions") return "cpa";
    if (type === "Target Impression Share") return "tis";
    const mode = (activation.mode ?? "").split(" - ")[0].trim().toLowerCase();
    if (mode === "active mode") return "active";
    if (mode === "ko bidding manual") return "ko_manual";
    return "ko_auto";
}

export interface ParametersTable {
    title: string;
    columns: { key: string; label: string; numeric: boolean }[];
    rows: { id: number; cells: Record<string, string> }[];
}

/**
 * One table per strategy of the "View" modal, with the columns of the design, from
 * the Signal activations only: the Google Ads strategy (G-READ) has no parameters.
 * `campaignName` fills a line without its campaign name.
 */
export function parametersTables(strategy: StrategyActivation[] | null | undefined, campaignName?: string): ParametersTable[] {
    const activations = (strategy ?? []).filter(isSignalActivation);
    const groups = new Map<string, StrategyActivation[]>();
    activations.forEach((activation) => {
        const key = strategyGroup(activation);
        groups.set(key, [...(groups.get(key) ?? []), activation]);
    });
    return Array.from(groups.entries()).map(([key, group]) => {
        const { title, columns } = STRATEGY_GROUPS[key];
        return {
            title: title(group[0]),
            columns: columns.map(({ key: columnKey, label, numeric }) => ({ key: columnKey, label, numeric })),
            rows: group.map((activation, index) => ({
                // No scraping id before the switch of a scheduled experiment (lot 3)
                id: activation.scraping_id ?? -(index + 1),
                cells: Object.fromEntries(
                    columns.map((column) => {
                        const value = column.key === "campaign" ? first(column.value(activation), campaignName) : column.value(activation);
                        return [column.key, first(value) === undefined ? "--" : asText(value)];
                    })
                ),
            })),
        };
    });
}
